import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (user) navigate(user.role === "patient" ? "/portal" : "/staff", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    const dest = res.user.role === "patient" ? "/portal" : "/staff";
    navigate(loc.state?.from?.pathname || dest, { replace: true });
  };

  const quick = (e, p) => {
    setEmail(e);
    setPassword(p);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex flex-col">
        <div className="p-6 sm:p-10">
          <Link to="/">
            <Logo size={48} withText />
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 pb-10">
          <div className="w-full max-w-md">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Secure Sign-In
            </div>
            <h1
              className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900 mb-2"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Welcome back.
            </h1>
            <p className="text-sm text-zinc-500 mb-8">
              Staff, doctors, and patients - same door, different views.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-zinc-900"
                  placeholder="you@email.com"
                  data-testid="login-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">
                  Password
                </label>
                <div className="relative mt-2">
                  <input
                    type={show ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-zinc-900 pr-10"
                    placeholder="........"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  >
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {err && (
                <div className="text-sm text-[#E60000] font-medium border border-red-200 bg-red-50 px-3 py-2 rounded-sm">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#CC0000] disabled:opacity-60 text-white font-semibold rounded-sm px-6 py-3 transition-colors"
                data-testid="login-submit"
              >
                {busy ? "Signing in..." : "Sign in"} <ArrowRight size={16} weight="bold" />
              </button>
            </form>

            <div className="mt-6 text-sm text-zinc-500">
              New patient?{" "}
              <Link to="/register" className="text-zinc-900 font-semibold underline underline-offset-2">
                Create your account
              </Link>
            </div>

            <div className="mt-10 border-t border-zinc-200 pt-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-bold">
                Quick test accounts
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                  ["Admin", "admin@drtayyebdental.com", "StrongPassword123"],
                  ["Staff", "staff@drtayyebdental.com", "StaffPassword123"],
                  ["Patient", "patient@drtayyebdental.com", "PatientPassword123"],
                  ["Doctor", "doctor@drtayyebdental.com", "DoctorPassword123"],
                ].map(([label, e, p]) => (
                  <button
                    type="button"
                    key={e}
                    onClick={() => quick(e, p)}
                    className="text-left border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-900 transition-colors"
                    data-testid={`quick-${label.toLowerCase()}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                      {label}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{e}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-[#0A0A0A] text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1764437358359-101340cbd5f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNsZWFuJTIwd2hpdGUlMjByZWQlMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3ODQ2OTY3MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Logo size={56} withText dark />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">
              The dental practice OS
            </div>
            <div
              className="text-4xl xl:text-5xl font-black tracking-tighter leading-[1] max-w-md"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Capture leads.<br />
              Track patients.<br />
              <span className="text-[#E60000]">Grow the clinic.</span>
            </div>
            <div className="mt-8 text-sm text-zinc-400 max-w-md leading-relaxed">
              From the first phone call to the final receipt - Dr Tayyeb&apos;s CRM keeps every interaction on one canvas.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
